import { useMemo, useEffect, useCallback, useRef, useReducer, memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Steps, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  Modal as EnsoModal,
  notification,
  ImportUploadFile,
  ImportTableDataRow,
  ImportCsvRow,
  transformToImportSchemaColumn,
  rejectionError,
  AdditionalFields,
} from '@ohpen/enso-ui';
import { splitArrayIntoChunks, processCSVObject } from '@ohpen/react-utils';
import { changeRequestButtonMessage } from '@pmp/utils';
import {
  ActionStatus,
  FieldType,
  ImportColumnMapping,
  ImportDataResponse,
  ImportDataRowRequest,
  ImportDataRowResponse,
  ImportResult,
  ImportSchemaColumn,
  ImportSchemaResponse,
  importsGetImportData,
  importsGetImport,
  importSchemasGetAnImportSchema,
  importsAddImportRows,
  importsCreateImport,
  importsSubmitForReview,
} from '@pmp/api';
import { useAdditionalFieldsStore, useValueListsStore } from '@pmp/stores';
import { useFeatureToggle } from '@pmp/hooks';

import { PRODUCTS_VALUE_LIST_NAMES } from '../../products';
import { EXCHANGE_RATE_VALUE_LIST_NAMES } from '../../exchangeRates';
import { HeadersFormData, ImportModalProps, ImportColumnMappingEntries, ImportModalState, ImportModalAction } from '../interfaces';
import styles from '../styles.module.css';
import HeaderMapping from './HeaderMapping';
import ErrorsTable from './ErrorsTable';

const { Text } = Typography;

const chunkSize = 50;

const entitiesValueListNames: { [key: string]: FieldType[] } = {
  PRODUCTS: PRODUCTS_VALUE_LIST_NAMES,
  EXCHANGE_RATES: EXCHANGE_RATE_VALUE_LIST_NAMES,
};

const getInitialState = <T,>(): ImportModalState<T> => ({
  csvHeaders: [],
  csvData: null,
  tableData: [],
  columnsMapping: [],
  columnsSchema: [],
  additionalFields: {},
  extendedSchemaColumns: [],
  step: null,
  confirmGo: null,
  importId: null,
  isLoading: false,
  isValidating: false,
  validationFeedback: null,
  isValid: false,
  activeCell: null,
  fileName: '',
  sheetName: 'Sheet 1',
});

// NOSONAR
function reducer<T>(prevState: ImportModalState<T>, action: ImportModalAction<T>): ImportModalState<T> {
  switch (action.type) {
    case 'open':
      if (action.step === undefined || action.importId === undefined || !action.fileName) throw new Error('Invalid action provided');
      return {
        ...getInitialState<T>(),
        isLoading: !!action.step,
        step: action.step,
        importId: action.importId,
        fileName: action.fileName,
      };
    case 'setActiveCell':
      if (action.activeCell === undefined) throw new Error(`Invalid action provided ${action.type}`);
      return { ...prevState, activeCell: action.activeCell };
    case 'setStep':
      if (action.step === undefined) throw new Error('Invalid action provided');
      return { ...prevState, step: action.step, confirmGo: null };
    case 'setValidations':
      if (!action.validationFeedback || action.isValid === undefined) throw new Error('Invalid action provided');
      return {
        ...prevState,
        validationFeedback: action.validationFeedback,
        isValid: action.isValid,
        isValidating: false,
      };
    case 'setIsLoading':
      if (action.isLoading === undefined) throw new Error('Invalid action provided');
      return { ...prevState, isLoading: action.isLoading };
    case 'setIsValidating':
      if (action.isValidating === undefined) throw new Error('Invalid action provided');
      return { ...prevState, isValidating: action.isValidating };
    case 'setImportId':
      if (action.importId === undefined) throw new Error('Invalid action provided');
      return { ...prevState, importId: action.importId };
    case 'setColumnsMapping':
      if (!action.columnsMapping) throw new Error('Invalid action provided');
      return { ...prevState, columnsMapping: action.columnsMapping };
    case 'setColumnsSchema':
      if (!action.columnsSchema || !action.additionalFields || !action.extendedSchemaColumns) throw new Error('Invalid action provided');
      return {
        ...prevState,
        columnsSchema: action.columnsSchema,
        additionalFields: action.additionalFields,
        extendedSchemaColumns: action.extendedSchemaColumns,
      };
    case 'setInitialTableData':
      if (!action.tableData || !action.columnsMapping) throw new Error('Invalid action provided');
      return {
        ...prevState,
        tableData: action.tableData,
        columnsMapping: action.columnsMapping,
        isLoading: action.isLoading !== undefined ? action.isLoading : prevState.isLoading,
      };
    case 'setTableData':
      if (!action.tableData) throw new Error('Invalid action provided');
      return { ...prevState, tableData: action.tableData };
    case 'setConfirmGo':
      if (action.confirmGo === undefined) throw new Error('Invalid action provided');
      return { ...prevState, confirmGo: action.confirmGo };
    case 'onDeleteRows':
      if (!action.validationFeedback || !action.tableData || action.isValid === undefined) throw new Error('Invalid action provided');
      return {
        ...prevState,
        tableData: action.tableData,
        validationFeedback: action.validationFeedback,
        isValid: action.isValid,
        isValidating: false,
      };
    case 'onFinishUpload':
      if (action.csvHeaders === undefined || action.csvData === undefined || !action.fileName || !action.sheetName)
        throw new Error('Invalid action provided');
      return {
        ...prevState,
        csvHeaders: action.csvHeaders,
        csvData: action.csvData,
        fileName: action.fileName,
        sheetName: action.sheetName,
        step: 1,
      };
    default:
      throw new Error(`No action provided or unknown action ${action.type}`);
  }
}

export const ImportModal = memo(
  <T,>({ importEntity, importId, creationTime, status, readOnly, onDeleteImport, onClose }: ImportModalProps): ReactElement | null => {
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [headersForm] = Form.useForm();
    const [state, dispatch] = useReducer(reducer, getInitialState());
    const { valueLists, getValueLists } = useValueListsStore();
    const { getAdditionalFields } = useAdditionalFieldsStore();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const getAllRows = useCallback(
      (importId: string | null) => {
        if (!importId) return;
        let allRows: ImportDataRowResponse[] = [];
        const getImportDataPage = (paginationToken?: string): Promise<ImportDataRowResponse[]> =>
          importsGetImportData({ path: { importId: importId }, query: { paginationToken: paginationToken } }).then(
            ({ data }: { data: ImportDataResponse }) => {
              const {
                rows,
                pageOptions: { paginationToken },
              } = data;

              allRows = [...allRows, ...rows];
              return paginationToken ? getImportDataPage(paginationToken) : Promise.resolve(allRows);
            },
          );
        const getColumnsMapping = () => {
          return importsGetImport({ path: { importId: importId } }).then(({ data }: { data: ImportResult }) => data.columns);
        };

        Promise.all([getImportDataPage(), getColumnsMapping()])
          .then(([tableData, columnsMapping]) => {
            dispatch({
              type: 'setInitialTableData',
              tableData: tableData as ImportTableDataRow<T>[],
              columnsMapping,
              isLoading: false,
            });
          })
          .catch(err => {
            notification().error({ message: 'global.errors.gettingData' });
            console.error(err);
            onClose(true);
          });
      },
      [onClose],
    );

    useEffect(() => {
      if (state.step !== null) return;
      dispatch({
        type: 'open',
        step: importId ? 2 : 0,
        importId: importId ?? null,
        fileName: `Import ${importEntity.toLocaleLowerCase()} ${dayjs(creationTime).format('YYYY-MM-DD HH:mm')}`,
      });

      Promise.all([importSchemasGetAnImportSchema({ path: { importEntityType: importEntity } }), getAdditionalFields(importEntity)])
        .then(([{ data }, additionalFields]: [{ data: ImportSchemaResponse }, AdditionalFields]) => {
          const columnsSchema = data?.columns ?? [];
          const transformedArray =
            !additionalFields || Object.keys(additionalFields).length === 0 ? [] : transformToImportSchemaColumn(additionalFields);
          const extendedSchemaColumns = [...columnsSchema, ...transformedArray];

          dispatch({ type: 'setColumnsSchema', columnsSchema, additionalFields: additionalFields ?? null, extendedSchemaColumns });
        })
        .catch((err: unknown) => {
          console.error(err);
          notification().error({ message: t('global.errors.gettingImportSchema') });
          onClose(true);
        });

      getAllRows(importId);
    }, [creationTime, getAdditionalFields, getAllRows, importEntity, importId, onClose, readOnly, state.fileName, state.step, status, t]);

    useEffect(() => {
      entitiesValueListNames[importEntity] && getValueLists({ fetchIfMissing: entitiesValueListNames[importEntity] });
    }, [getValueLists, importEntity]);

    const addAllRows = useCallback((importId: string, initialTableData: ImportDataRowRequest[]) => {
      if (!importId) return Promise.reject(rejectionError('global.errors.noImportId'));
      dispatch({ type: 'setImportId', importId });
      const chunks: ImportDataRowRequest[][] = splitArrayIntoChunks(initialTableData, chunkSize);
      const promises = chunks.map((data: ImportDataRowRequest[]) => importsAddImportRows({ path: { importId: importId }, body: { data } }));

      return Promise.all(promises);
    }, []);

    const onPrev = useCallback(() => {
      if (!state.step) return;
      dispatch({ type: 'setConfirmGo', confirmGo: state.step - 1 });
    }, [state.step]);

    const setInitialValue = useCallback(
      (formData: HeadersFormData): ImportTableDataRow<T>[] => {
        return state.csvData
          ? (state.csvData
              ?.map((row: ImportCsvRow) => {
                const newTableRow: ImportTableDataRow<T> = { rowNumber: row.rowNumber, fields: {} as T };
                const fields = processCSVObject(row.fields, state.columnsSchema ?? []) ?? row.fields;
                Object.keys(formData).forEach((key: string) => {
                  newTableRow.fields[key as keyof T] = fields[formData[key]] as T[keyof T];
                });
                return newTableRow;
              })
              .filter(row => Object.values(row.fields as Record<string, unknown>).some(value => value !== undefined)) ?? [])
          : (state.tableData as ImportTableDataRow<T>[]);
      },
      [state.columnsSchema, state.csvData, state.tableData],
    );

    const goToValidateStep = useCallback(() => {
      let initialTableData: ImportTableDataRow<T>[] = [];

      dispatch({ type: 'setIsLoading', isLoading: true });
      headersForm
        .validateFields()
        .then((formData: HeadersFormData) => {
          const columnsMapping: ImportColumnMapping[] = Object.entries(formData)
            .filter(([key, clientKey]: ImportColumnMappingEntries) => clientKey)
            .map(([key, clientKey]: ImportColumnMappingEntries) => ({
              key,
              clientKey,
            }));
          initialTableData = setInitialValue(formData);
          dispatch({
            type: 'setInitialTableData',
            tableData: initialTableData,
            columnsMapping,
          });
          return importsCreateImport({ body: { type: importEntity, columns: columnsMapping } });
        })
        .then(({ data }: { data: ImportResult }) => addAllRows(data.importId, initialTableData))
        .then(() => dispatch({ type: 'setStep', step: (state.step ?? 0) + 1 }))
        .catch(err => {
          if (err.errorFields?.length) {
            notification().error({ message: t('global.errors.fillAllRequired') });
          } else {
            notification().error({ message: t('global.errors.creatingImport') });
          }
          console.error(err);
        })
        .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
    }, [headersForm, state.step, importEntity, addAllRows, t, setInitialValue]);

    const submitForReview = useCallback(() => {
      if (!state.importId) {
        notification().error({ message: t('global.errors.submitForApproval') });
        return;
      }
      dispatch({ type: 'setIsLoading', isLoading: true });
      importsSubmitForReview({ path: { importId: state.importId } })
        .then(() => {
          notification().success({ message: t('global.notifications.importSubmitted') });
          onClose();
        })
        .catch(err => {
          console.error(err);
          notification().error({ message: t('global.errors.submitForApproval') });
        })
        .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
    }, [onClose, state.importId, t]);

    const onNext = useCallback(() => {
      if (state.step === 0) {
        dispatch({ type: 'setStep', step: state.step + 1 });
      } else if (state.step === 1) {
        goToValidateStep();
      } else if (state.step === 2) {
        submitForReview();
      }
    }, [goToValidateStep, state.step, submitForReview]);

    const onCancelBack = useCallback(() => {
      dispatch({ type: 'setConfirmGo', confirmGo: null });
    }, []);

    const onConfirmBack = useCallback(() => {
      if (state.confirmGo === null) return;
      dispatch({ type: 'setStep', step: state.confirmGo });
    }, [state.confirmGo]);

    const onChangeStep = useCallback(
      (goStep: number) => {
        if (goStep === 2) return goToValidateStep();
        if ((state.step === 1 && goStep === 0) || state.step === 2) return dispatch({ type: 'setConfirmGo', confirmGo: goStep });
        dispatch({ type: 'setStep', step: goStep });
      },
      [goToValidateStep, state.step],
    );

    const onFinishUpload = useCallback((csvHeaders: string[], csvData: ImportCsvRow[], fileName: string, sheetName: string) => {
      dispatch({ type: 'onFinishUpload', csvHeaders, csvData, fileName, sheetName });
    }, []);

    const onDeleteAllRows = useCallback(() => {
      onDeleteImport();
      onClose(true);
    }, [onClose, onDeleteImport]);

    const handleOnClose = useCallback(() => {
      onClose(true);
    }, [onClose]);

    const nextDisabled: boolean = useMemo(
      () => state.step === 0 || (state.step === 1 && !state.columnsSchema) || (state.step === 2 && !state.isValid) || state.isLoading,
      [state.columnsSchema, state.isLoading, state.isValid, state.step],
    );

    const prevDisabled: boolean = useMemo(
      () => state.step === 0 || (state.step === 1 && !state.columnsSchema) || state.isLoading,
      [state.columnsSchema, state.isLoading, state.step],
    );

    const stepItems = useMemo(
      () => [
        { title: t('global.labels.uploadFile') },
        { title: t('global.labels.matchColumns'), disabled: state.step === 0 },
        { title: t('global.labels.validate'), disabled: state.step === 0 },
      ],
      [state.step, t],
    );

    const columnsHeaders = useMemo(() => {
      return state.csvHeaders.length ? state.csvHeaders : state.columnsMapping.map(x => x.key);
    }, [state.csvHeaders, state.columnsMapping]);

    const stepperContent = useMemo(() => {
      if (state.step === 0) return <ImportUploadFile onFinishUpload={onFinishUpload} />;
      if (state.step === 1) {
        return (
          <HeaderMapping
            importId={state.importId}
            importEntity={importEntity}
            form={headersForm}
            headers={columnsHeaders}
            schema={state.extendedSchemaColumns ?? []}
            columns={state.columnsMapping}
          />
        );
      }
      if (state.step === 2 && state.importId) {
        const matchedColumns = state.columnsMapping.map(({ key }) => key);
        const schema: ImportSchemaColumn[] | undefined =
          state.extendedSchemaColumns?.filter(column => matchedColumns.includes(column.key)) ?? [];

        return (
          <ErrorsTable
            importEntity={importEntity}
            importId={state.importId}
            tableData={state.tableData}
            activeCell={state.activeCell}
            schema={schema}
            fileName={state.fileName}
            sheetName={state.sheetName}
            isLoading={state.isLoading}
            dispatch={dispatch}
            onDeleteAllRows={onDeleteAllRows}
            validationFeedback={state.validationFeedback}
            valueLists={valueLists ?? {}}
            readOnly={readOnly}
          />
        );
      }
      return null;
    }, [
      state.step,
      state.importId,
      state.extendedSchemaColumns,
      state.columnsMapping,
      state.tableData,
      state.activeCell,
      state.fileName,
      state.sheetName,
      state.isLoading,
      state.validationFeedback,
      onFinishUpload,
      importEntity,
      headersForm,
      columnsHeaders,
      onDeleteAllRows,
      valueLists,
      readOnly,
    ]);

    const title = useMemo(() => {
      if (readOnly) {
        return status === ActionStatus.DRAFT ? t('global.labels.viewDraftImport') : t('global.labels.importResults');
      }
      return t('global.labels.importX', { x: t(`pmp.entities.${importEntity}`) });
    }, [importEntity, readOnly, status, t]);

    if (state.step === null) return null;

    return (
      <>
        <Modal
          className={styles.modal}
          title={title}
          open={true}
          onCancel={handleOnClose}
          destroyOnHidden={true}
          width={'95vw'}
          footer={
            readOnly ? (
              <Button key="close" onClick={handleOnClose}>
                {t('global.buttons.close')}
              </Button>
            ) : (
              [
                <Button key="back" onClick={onPrev} disabled={prevDisabled}>
                  {t('global.buttons.previous')}
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={onNext}
                  disabled={nextDisabled}
                  loading={state.isValidating || state.isLoading}
                >
                  {state.step === 2 ? changeRequestButtonMessage(t, enableFourEyes) : t('global.buttons.next')}
                </Button>,
              ]
            )
          }
        >
          <Steps current={state.step} items={stepItems} onChange={onChangeStep} />
          <div className="import-modal-content-container" ref={contentRef}>
            {stepperContent}
          </div>
        </Modal>
        <Modal title="Confirm" open={state.confirmGo !== null} onOk={onConfirmBack} onCancel={onCancelBack} width={400} zIndex={3000}>
          <Text>{t('global.labels.confirmBack')}</Text>
          <Text>{t('global.labels.changesStepLost')}</Text>
        </Modal>
      </>
    );
  },
);
